import { useEffect, useMemo, useRef, useState } from 'react';
import { quotaService } from '../services/quotaService';

const isFilled = (value) => {
  if (value === null || value === undefined) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'string') return value.trim().length > 0;
  if (typeof value === 'object') return true;
  return true;
};

const simplifyFieldValue = (value) => {
  if (value === null || value === undefined) return value;
  if (Array.isArray(value)) {
    return value
      .map(simplifyFieldValue)
      .filter((v) => v !== null && v !== undefined);
  }
  if (typeof value === 'object') {
    if (value.label) return value.label;
    if (value.value) return value.value;
    if (value.key) return value.key;
    if (value.objectKey) return value.objectKey;
    return value;
  }
  return value;
};

const buildCustomFieldsPayload = (formData) => {
  const out = {};
  Object.entries(formData || {}).forEach(([k, v]) => {
    if (!String(k).startsWith('customfield_')) return;
    out[k] = simplifyFieldValue(v);
  });
  return out;
};

export function useQuotaPanelState({
  enabled,
  serviceDeskId,
  requestTypeId,
  requiredFieldIds = [],
  formData,
} = {}) {
  const [hasQuota, setHasQuota] = useState(false);
  const [countLoading, setCountLoading] = useState(false);
  const [checkLoading, setCheckLoading] = useState(false);
  const [checkData, setCheckData] = useState(null);
  const [checkError, setCheckError] = useState('');

  const ready = useMemo(() => {
    if (!enabled) return false;
    if (!Number.isFinite(Number(serviceDeskId))) return false;
    if (!Number.isFinite(Number(requestTypeId))) return false;
    if (!Array.isArray(requiredFieldIds) || requiredFieldIds.length === 0) return false;
    return requiredFieldIds.every((fid) => isFilled(formData?.[fid]));
  }, [enabled, serviceDeskId, requestTypeId, requiredFieldIds, formData]);

  const payloadFields = useMemo(() => buildCustomFieldsPayload(formData || {}), [formData]);

  const blocking = Boolean(checkData?.blocking) && checkData?.status === 'exceeded';
  const checking = Boolean(countLoading || checkLoading);

  // 1) On mount / when requestType changes, check if quotas exist
  useEffect(() => {
    if (!enabled || !Number.isFinite(Number(serviceDeskId)) || !Number.isFinite(Number(requestTypeId))) {
      setHasQuota(false);
      setCheckData(null);
      setCheckError('');
      setCountLoading(false);
      setCheckLoading(false);
      return;
    }

    const controller = new AbortController();
    setCountLoading(true);

    quotaService
      .count({ serviceDeskId, requestTypeId, signal: controller.signal })
      .then((count) => {
        if (controller.signal.aborted) return;
        setHasQuota(Number(count) > 0);
      })
      .catch((e) => {
        if (controller.signal.aborted) return;
        setHasQuota(false);
        setCheckError(e?.message || 'Quota count failed');
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setCountLoading(false);
      });

    return () => controller.abort();
  }, [enabled, serviceDeskId, requestTypeId]);

  // 2) When all required fields are filled and quotas exist, run the quota check
  const lastKeyRef = useRef('');
  useEffect(() => {
    if (!enabled || !hasQuota) return;
    if (!ready) {
      setCheckData(null);
      setCheckError('');
      setCheckLoading(false);
      return;
    }

    const key = JSON.stringify({ serviceDeskId, requestTypeId, payloadFields });
    if (key === lastKeyRef.current) return;
    lastKeyRef.current = key;

    const controller = new AbortController();
    setCheckLoading(true);
    setCheckError('');

    quotaService
      .check({ serviceDeskId, requestTypeId, fields: payloadFields, signal: controller.signal })
      .then((data) => {
        if (controller.signal.aborted) return;
        setCheckData(data);
      })
      .catch((e) => {
        if (controller.signal.aborted) return;
        setCheckData(null);
        setCheckError(e?.message || 'Quota check failed');
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setCheckLoading(false);
      });

    return () => controller.abort();
  }, [enabled, hasQuota, ready, serviceDeskId, requestTypeId, payloadFields]);

  return {
    hasQuota,
    countLoading,
    checkLoading,
    checkData,
    checkError,
    ready,
    payloadFields,
    checking,
    blocking,
  };
}

